import { useId } from "react";

export const cardShadow = "shadow-[1px_1px_0px_0px_#1c1b2e]";
export const surfaceBase = `rounded-[20px] border border-surface-border ${cardShadow}`;

export function Card({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "strong" | "accent";
}) {
  const bgClass =
    tone === "strong" ? "bg-card-strong" : tone === "accent" ? "bg-[#e8fbf6]" : "bg-card";

  return (
    <div className={`${bgClass} ${surfaceBase} p-4 flex flex-col gap-3.5 overflow-hidden relative`}>
      <div className="absolute -top-15 -right-10 w-30 h-30 rounded-full bg-[rgba(255,45,126,0.04)]" />
      {children}
    </div>
  );
}

export function Field({
  label,
  value,
  onChangeText,
  placeholder,
  multiline = false,
  secureTextEntry = false,
  type = "text",
}: {
  label: string;
  value: string;
  onChangeText?: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  secureTextEntry?: boolean;
  type?: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]"
      >
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChangeText?.(e.target.value)}
          placeholder={placeholder}
          className="bg-white rounded-2xl border border-text px-3.5 py-3 text-text text-sm font-inherit min-h-[6.25rem] resize-y"
        />
      ) : (
        <input
          id={id}
          type={secureTextEntry ? "password" : type}
          value={value}
          onChange={(e) => onChangeText?.(e.target.value)}
          placeholder={placeholder}
          className="bg-white rounded-2xl border border-text px-3.5 py-3 text-text text-sm font-inherit w-full"
        />
      )}
    </div>
  );
}

export function Button({
  label,
  kind = "primary",
  type = "button",
  onPress,
  disabled,
}: {
  label: string;
  kind?: "primary" | "secondary" | "ghost";
  type?: "button" | "submit" | "reset";
  onPress?: () => void;
  disabled?: boolean;
}) {
  const baseClass =
    "rounded-2xl py-3.5 px-5 items-center justify-center border border-text flex flex-row font-inherit cursor-pointer active:translate-x-[0.0625rem] active:translate-y-[0.0625rem]";

  const variantClass =
    kind === "secondary"
      ? "bg-amber shadow-[1px_1px_0px_0px_#1c1b2e]"
      : kind === "ghost"
        ? "bg-transparent border-[rgba(28,27,46,0.1)]"
        : "bg-aqua shadow-[1px_1px_0px_0px_#1c1b2e]";

  const textClass =
    kind === "secondary"
      ? "text-text text-sm font-extrabold"
      : kind === "ghost"
        ? "text-text-muted text-sm font-extrabold"
        : "text-white text-sm font-extrabold";

  return (
    <button
      type={type}
      onClick={onPress}
      disabled={disabled}
      className={`${baseClass} ${variantClass}`}
    >
      <span className={textClass}>{label}</span>
    </button>
  );
}

export function Pill({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "neutral" | "success" | "warning" | "accent";
}) {
  const accentPink = "bg-[rgba(255,45,126,0.08)] border-[rgba(255,45,126,0.18)] text-aqua";
  const pillStyle =
    tone === "accent" || tone === "success"
      ? accentPink
      : tone === "warning"
        ? "bg-[rgba(255,193,7,0.1)] border-[rgba(255,193,7,0.2)] text-text-muted"
        : "bg-[rgba(0,0,0,0.04)] border-[rgba(0,0,0,0.04)] text-text-muted";

  return (
    <span
      className={`self-start rounded-full px-2.5 py-1 border ${pillStyle} text-[0.625rem] font-bold uppercase tracking-[0.05em]`}
    >
      {label}
    </span>
  );
}

export function SelectField({
  label,
  value,
  options,
  onValueChange,
}: {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onValueChange: (value: string) => void;
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className="bg-white rounded-2xl border border-text px-3.5 py-3 text-text text-sm font-inherit appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function ArrowLeft({ className = "" }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <title>Back</title>
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

export function ArrowRight({ className = "" }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <title>Next</title>
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  );
}

export function DateField({
  label,
  value,
  onValueChange,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]"
      >
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className="bg-white rounded-2xl border border-text px-3.5 py-3 text-text text-sm font-inherit"
      />
    </div>
  );
}
